import { takeLatest, put } from 'redux-saga/effects';

import { logOut } from '../auth/actions';
import UserPermissionService from '../../../services/UserPermissionService';
import {
  initUserPermissionsRequest,
  initUserPermissionsSuccess,
  initUserPermissionsFail,
  getUserPermissionsRequest,
  getUserPermissionsSuccess,
  getUserPermissionsFail,
  getUserPermissionsListRequest,
  getUserPermissionsListSuccess,
  getUserPermissionsListFail,
  setUserPermissionsListDirty,
  createUserPermissionsRequest,
  createUserPermissionsSuccess,
  createUserPermissionsFail,
  saveUserPermissionsRequest,
  saveUserPermissionsSuccess,
  saveUserPermissionsFail,
  saveUserPermissionsRequestDirty,
  saveUserPermissionsSuccessDirty,
  saveUserPermissionsFailDirty,
  deleteUserPermissionsRequest,
  deleteUserPermissionsSuccess,
  deleteUserPermissionsFail,
  inviteUserPermissionsRequest,
  inviteUserPermissionsSuccess,
  setUserPermissionsListRedirect,
  resendInviteUserPermissionsRequest,
  resendInviteUserPermissionsSuccess,
  setOptionSender,
  setOptionRequestRegistration,
  setOptionRequestBilling,
  setOptionSendEmail,
  getUserPermissionsListByUserRequest,
  getUserPermissionsListByUserSuccess,
  getUserPermissionsListByUserFail,
  clearUserPermissionsListByUser,
  clearUserPermissionsList,
} from './actions';
import { notify } from '../notifications/actions';
import { appInit } from '../init/actions'
import { apiCall, forEachGen } from '../../effects';
import { getErrorNotification } from '../../util';
import { State as StateDefaultOptions } from './reducers/defaultOptions';

/* eslint-disable @typescript-eslint/no-explicit-any */

function* getUserPermissionsList({ payload }: ReturnType<typeof getUserPermissionsListRequest>) {
  try {
    const list = yield apiCall(UserPermissionService.list, payload);
    yield put(getUserPermissionsListSuccess(list));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getUserPermissionsListFail());
  }
}

function* getUserPermissions({ payload }: ReturnType<typeof getUserPermissionsRequest>) {
  try {
    const response = yield apiCall(UserPermissionService.get, payload);
    yield put(getUserPermissionsSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getUserPermissionsFail());
  }
}

function* saveUserPermissions({ payload }: ReturnType<typeof saveUserPermissionsRequest>) {
  try {
    const response = yield apiCall(UserPermissionService.save, payload.id, payload);
    yield put(saveUserPermissionsSuccess(response));
    if(payload.invite){
      yield put(inviteUserPermissionsRequest(response));
    } else {
      yield put(setUserPermissionsListRedirect(true));
    }
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(saveUserPermissionsFail());
  }
}

function* saveUserPermissionsDirty({ payload }: ReturnType<typeof saveUserPermissionsRequestDirty>) {
  try {
    const response = yield apiCall(UserPermissionService.save, payload.id, payload);
    yield put(saveUserPermissionsSuccessDirty(response));
    yield put(setUserPermissionsListDirty(true));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(saveUserPermissionsFailDirty());
  }
}

function* createUserPermissions({ payload }: ReturnType<typeof createUserPermissionsRequest>) {
  try {
    const arrayEmails = payload.email.split(',');
    const userPermissionDataArray = arrayEmails.map((email) => {
      return { ...payload, email };
    });

    const createUserPermissionsResponse = yield forEachGen(
      userPermissionDataArray,
      (data) => apiCall(UserPermissionService.create, data),
    );


    const userPermissionsSuccess = createUserPermissionsResponse.filter((item: any) => !item.error);

    yield forEachGen(
      userPermissionsSuccess,
      (data) => put(createUserPermissionsSuccess(data)),
    );

    // redirect
    yield put(setUserPermissionsListRedirect(true));

    const inviteUsers = userPermissionsSuccess.filter((item: any) => item.invite);

    const inviteUsersResponse = yield forEachGen(
      inviteUsers,
      (data) => apiCall(UserPermissionService.invite, data),
    );

    const userPermissionsError = createUserPermissionsResponse.filter((item: any) => item.error);
    const inviteUsersError = inviteUsersResponse.filter((item: any) => item.error);

    let infoMessage = '';
    let notificationStatus: 'success' | 'error' = 'success';
    if (userPermissionsSuccess.length) {
      infoMessage += `${userPermissionsSuccess.length} members successfully added. \n`;
    }

    if (userPermissionsError.length) {
      const failureEmails = userPermissionsError.map((item: any) => `${item?.data?.email} - ${item.error}`);
      notificationStatus = 'error';
      infoMessage += `Members failure:\n ${failureEmails.join(',\n')}\n`;
    }

    if (inviteUsersError.length) {
      const failureEmails = inviteUsersError.map((item: any) => item?.data?.email);
      notificationStatus = 'error';
      infoMessage += `A new invitations failure: ${failureEmails.join(', ')}`;
    }

    yield put(notify({
      type: notificationStatus,
      message: infoMessage,
    }));
  } catch (e) {
    yield put(initUserPermissionsSuccess(payload));
    yield put(createUserPermissionsFail());
    yield put(notify(getErrorNotification(e)));
  }
}

function* deleteUserPermissions({ payload }: ReturnType<typeof deleteUserPermissionsRequest>) {
  try {
    yield apiCall(UserPermissionService.delete, payload);
    yield put(deleteUserPermissionsSuccess());
    yield put(setUserPermissionsListDirty(true));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(deleteUserPermissionsFail());
  }
}

function* getTemplateUserPermissions(){
  try {
    const response = yield apiCall(UserPermissionService.template);
    yield put(initUserPermissionsSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(initUserPermissionsFail());
  }
}

function* inviteUserPermissions({ payload }: ReturnType<typeof inviteUserPermissionsRequest>) {
  try {
    yield apiCall(UserPermissionService.invite, payload);
    yield put(inviteUserPermissionsSuccess());
    yield put(notify({
      type: 'success',
      message: 'A new invitation has been sent',
    }));
    yield put(setUserPermissionsListRedirect(true));
  } catch (e) {
    yield put(notify({
      type: 'error',
      message: 'There was a problem sending the invitation',
    }));
  }
}

function* resendInviteUserPermissions({ payload }: ReturnType<typeof resendInviteUserPermissionsRequest>) {
  try {
    yield apiCall(UserPermissionService.invite, payload);
    yield put(resendInviteUserPermissionsSuccess());
    yield put(notify({
      type: 'success',
      message: 'A new invitation has been sent',
    }));
    yield put(setUserPermissionsListDirty(true));
  } catch (e) {
    yield put(notify({
      type: 'error',
      message: 'There was a problem sending the invitation',
    }));
  }
}

const getDefaultOptions = (): StateDefaultOptions => {
  return JSON.parse(localStorage.getItem('default-options') as string);
};

const setDefaultOptions = (key: 'invite' | 'cc_sender' | 'request_registration' | 'request_billing_info', value: boolean): void => {
  const options = getDefaultOptions();
  options[key] = value;
  localStorage.setItem('default-options', JSON.stringify(options));
};

function* initDefaultOptions() {
  try {
    const defaultOptions = localStorage.getItem('default-options');
    if (defaultOptions !== null){
      const options: StateDefaultOptions = JSON.parse(defaultOptions);
      yield put(setOptionSendEmail(options.invite));
      yield put(setOptionRequestBilling(options.request_billing_info));
      yield put(setOptionRequestRegistration(options.request_registration));
      yield put(setOptionSender(options.cc_sender));
    } else {
      localStorage.setItem('default-options', JSON.stringify({
        invite: true,
        cc_sender: true,
        request_registration: true,
        request_billing_info: true
      }));
    }
  } catch (e) {
    console.log(e);
  }
}

function* getUserPermissionsListByUser({ payload }: ReturnType<typeof getUserPermissionsListByUserRequest>){
  try {
    const response = yield apiCall(UserPermissionService.listByUser, payload);
    yield put(getUserPermissionsListByUserSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getUserPermissionsListByUserFail());
  }
}

function* clearState() {
  yield put(clearUserPermissionsListByUser());
  yield put(clearUserPermissionsList());
}

function* setDefaultOptionSendEmail({ payload }: ReturnType<typeof setOptionSendEmail>){
  yield setDefaultOptions('invite', payload);
}

function* setDefaultOptionSender({ payload }: ReturnType<typeof setOptionSender>){
  yield setDefaultOptions('cc_sender', payload);
}

function* setDefaultOptionRegistration({ payload }: ReturnType<typeof setOptionRequestRegistration>){
  yield setDefaultOptions('request_registration', payload);
}

function* setDefaultOptionBilling({ payload }: ReturnType<typeof setOptionRequestBilling>){
  yield setDefaultOptions('request_billing_info', payload);
}

export default [
  takeLatest(appInit.type, initDefaultOptions),
  takeLatest(getUserPermissionsListRequest.type, getUserPermissionsList),
  takeLatest(getUserPermissionsRequest.type, getUserPermissions),
  takeLatest(saveUserPermissionsRequest.type, saveUserPermissions),
  takeLatest(createUserPermissionsRequest.type, createUserPermissions),
  takeLatest(deleteUserPermissionsRequest.type, deleteUserPermissions),
  takeLatest(initUserPermissionsRequest.type, getTemplateUserPermissions),
  takeLatest(saveUserPermissionsRequestDirty.type, saveUserPermissionsDirty),
  takeLatest(inviteUserPermissionsRequest.type, inviteUserPermissions),
  takeLatest(resendInviteUserPermissionsRequest.type, resendInviteUserPermissions),
  takeLatest(getUserPermissionsListByUserRequest.type, getUserPermissionsListByUser),
  takeLatest(setOptionSendEmail.type, setDefaultOptionSendEmail),
  takeLatest(setOptionSender.type, setDefaultOptionSender),
  takeLatest(setOptionRequestRegistration.type, setDefaultOptionRegistration),
  takeLatest(setOptionRequestBilling.type, setDefaultOptionBilling),
  takeLatest(logOut.type, clearState),
];
